import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { getVisitorId } from '../utils/abTest';
import EmailInput from './EmailInput';
import { sliderLabel, valueAtPosition, positionOfValue, rangeInputProps } from '../utils/sliderField';
import { findLocalArea, localAreaFromPlace } from '../utils/localArea';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';
import { introText, communityStartedIn } from '../utils/quickMatchIntroText';
import './QuickMatchHero.css';

// The 'quickmatch' hero flow (community.heroFlow): instead of a tagline and
// a Join button, the visitor answers a few of the community's own profile
// questions (its dropdowns and tick boxes) plus where they are, one at a
// time, right on the hero. We then show who in the community fits — or, if
// no one does yet, offer to email them when someone who fits joins.
// Backend: routes/quickMatch.js.

// C's "Before we run a search, would you like us to email you…?" step,
// between the last question and the search. Parked (25 Oct 2026): set to
// true to bring it back — the step's code is all still here.
const PRE_ASK = false;

const MIN_SEARCH_MS = 3900; // long enough for the lasso to tie up their answers and drop into the pulse

// Every tap here is recorded by the page analytics (hooks/
// useCommunityPageAnalytics.js) as a 'quickmatch' action, labelled from the
// element's data-qm. Things that aren't taps (a dropdown choice, what a
// search found, an email saved) are reported with this.
const track = (label) => {
  try { window.dispatchEvent(new CustomEvent('ic:track', { detail: { type: 'quickmatch', label } })); } catch { /* ignore */ }
};

const api = async (path, body) => {
  const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/quick-match${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Something went wrong — please try again.');
  return json;
};


// "Last person joined 52 hrs 13 min 07 sec ago", ticking every second.
const pad = (n) => String(n).padStart(2, '0');
export const LastJoined = ({ at }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const since = Math.max(0, Math.floor((now - new Date(at).getTime()) / 1000));
  if (!at || Number.isNaN(since)) return null;
  const d = Math.floor(since / 86400);
  const h = Math.floor((since % 86400) / 3600);
  const m = Math.floor((since % 3600) / 60);
  const s = since % 60;
  const hours = d * 24 + h; // no days — e.g. "26 hrs"
  const units = [
    hours > 0 && [hours, hours === 1 ? 'hr' : 'hrs'],
    [m, 'min'],
    [pad(s), 'sec'],
  ].filter(Boolean);
  return (
    <p className="qm-last-joined" aria-live="off">
      <span className="qm-last-joined-dot" aria-hidden="true" />
      <span>Last person joined</span>
      {units.map(([n, label]) => (
        <span key={label.replace(/s$/, '')} className="qm-last-joined-unit">
          <strong>{n}</strong> {label}
        </span>
      ))}
      <span>ago</span>
    </p>
  );
};

// community.quickMatchCountry ('UK') → the country code Google expects.
const COUNTRY_CODES = { uk: 'gb', 'united kingdom': 'gb', gb: 'gb', germany: 'de', de: 'de' };

// intro (hero version C): the card opens on a choice — join straight away,
// or have a quick look at who's near first — and says up front that the
// community is new and that connecting needs a profile (wording:
// utils/quickMatchIntroText.js).
// C_OPENS_ON_CHOICE false: C skips that choice and opens straight on "Find
// {people} near you", as if "See who's near me first" had been clicked (the
// page hides its tagline too). The choice screen's code is kept.
export const C_OPENS_ON_CHOICE = false;
const QuickMatchHero = ({ communityId, fields = [], peopleWord, retentionMonths = 0, country = '', floating = false, intro = false, matchLabel = '', onIntroLeave = null }) => {
  const word = peopleWord || 'people';
  // "See who's near me first": the choice dissolves away, then the first
  // question dissolves in (QuickMatchHero.css .qm-intro--leaving / --from-intro).
  const [introStage, setIntroStage] = useState(''); // '' | 'leaving' | 'arriving'
  const lookFirst = () => {
    if (introStage) return;
    setIntroStage('leaving');
    if (onIntroLeave) onIntroLeave(); // the page dissolves its tagline out too
    setTimeout(() => { setPhase('questions'); setIntroStage('arriving'); }, 450);
    setTimeout(() => setIntroStage(''), 450 + 900);
  };
  const intro_ = (key) => introText(key, { people: word, matchLabel, started: communityStartedIn(communityId) });
  const joinUrl = `/groupsearch/join/${communityId}`;
  // Steps: 0 = where, 1..n = each question, then searching / results / none / notified.
  const [step, setStep] = useState(0);
  const [place, setPlace] = useState('');
  const [coords, setCoords] = useState(null);
  // { localArea, coordinates } — the place resolved the same way as on
  // profiles (postcode → nearest town / London borough).
  const [found, setFound] = useState(null);
  // A whole country / region was given: an extra, skippable slide asks
  // which town or city in it. { name, countryCode } while it's showing.
  const [refining, setRefining] = useState(null);
  const refiningRef = useRef(null);
  refiningRef.current = refining;
  const acceptBroad = (where, how) => {
    setFound(where);
    setRefining({ name: where.localArea, countryCode: where.countryCode, where });
    setPlace('');
    setCoords(null);
    setError('');
    track(`${how} place: ${where.localArea} (asked which town)`);
  };
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState(intro && C_OPENS_ON_CHOICE ? 'intro' : 'questions');
  // C's end screens (no one near / members found): the parts dissolve in
  // one after another, each stacking under the last (all laid out from the
  // start, so the panel doesn't grow), the page scrolling down to show each
  // new one; then the email box. Parts are tagged data-film="0", "1"…, the
  // email box data-film="email". { shown: parts showing, done: email showing }.
  // When each part comes in (ms), per screen:
  const FILM_AT = {
    none: [0, 1000, 1800], // the news + email ask, widen, further away / join
    // the suggestions (who fits, the email ask, the fit, the members), Join, widen
    results: [0, 1200, 2200],
  };
  const [film, setFilm] = useState({ shown: 1, done: false });
  const filmTimers = useRef([]);
  const filmRef = useRef(null);
  useEffect(() => {
    filmTimers.current.forEach(clearTimeout);
    filmTimers.current = [];
    const at = intro && FILM_AT[phase];
    if (!at) return undefined;
    setFilm({ shown: 1, done: false });
    for (let i = 2; i <= at.length; i++) {
      filmTimers.current.push(setTimeout(() => setFilm({ shown: i, done: false }), at[i - 1]));
    }
    filmTimers.current.push(setTimeout(() => setFilm({ shown: at.length, done: true }), at[at.length - 1] + 1100));
    return () => filmTimers.current.forEach(clearTimeout);
  }, [intro, phase]); // eslint-disable-line react-hooks/exhaustive-deps
  const filmClass = (i) => (i < film.shown ? 'qm-film-in' : 'qm-film-wait');
  const [result, setResult] = useState(null);
  // No one near: the further-away members stay hidden until they tap
  // "Show riders further away".
  const [showFar, setShowFar] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [deleteAfter3, setDeleteAfter3] = useState(false);
  const [email, setEmail] = useState('');
  // C: email given on the "before we run a search" step — saved against the
  // search once it's run (the server needs its searchId), and again after
  // any later search, so it follows their latest answers.
  const [emailSaved, setEmailSaved] = useState(false);
  const notifyAfterSearch = useRef(false);
  const preAsked = useRef(false); // C: the "before we run a search" step has been shown
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // The match they tapped Connect on — shows the "sign up first" pop-up.
  const [connecting, setConnecting] = useState(null);
  const navigate = useNavigate();

  // Google place suggestions on the location box, as on profile location.
  // Loaded only when the box is first focused, so the hero stays light.
  const placeRef = useRef(null);
  const autocompleteRef = useRef(null);
  // The box is re-created when they come back to this step, so attach to
  // whichever element is on screen now.
  const attachAutocomplete = () => {
    const input = placeRef.current;
    if (!input || autocompleteRef.current?.input === input) return;
    loadGoogleMaps().then((google) => {
      if (placeRef.current !== input || autocompleteRef.current?.input === input) return;
      // On the "which town in France?" slide, only suggest places in France.
      const code = refiningRef.current?.countryCode || COUNTRY_CODES[String(country).trim().toLowerCase()];
      const ac = new google.maps.places.Autocomplete(placeRef.current, {
        types: ['geocode'],
        fields: ['formatted_address', 'geometry', 'name', 'types'],
        ...(code ? { componentRestrictions: { country: code } } : {}),
      });
      ac.addListener('place_changed', async () => {
        const picked = ac.getPlace();
        const label = picked?.formatted_address || picked?.name || placeRef.current?.value || '';
        setPlace(label);
        setCoords(null);
        const where = await localAreaFromPlace(picked).catch(() => null);
        if (where?.broad) {
          if (refiningRef.current) {
            setPlace('');
            setError(`Please pick a town or city in ${refiningRef.current.name} — or skip.`);
          } else {
            acceptBroad(where, 'Picked');
          }
          return;
        }
        setError('');
        setFound(where);
        track(where ? `Picked place: ${where.localArea}` : 'Picked place: not found');
      });
      autocompleteRef.current = { ac, input };
    }).catch(() => { /* typing + Next still works without suggestions */ });
  };

  // Keep the card in view as it changes height between steps.
  const cardRef = useRef(null);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // C's end screens are taller than the screen: just make sure their top
    // is showing (never scroll down into them — they read from the top).
    if (intro && (phase === 'results' || phase === 'none')) {
      if (r.top < 60 || r.top > window.innerHeight * 0.6) window.scrollBy({ top: r.top - 80, behavior: 'smooth' }); // clear of the top bar
      return;
    }
    if (r.top < 60 || r.bottom > window.innerHeight) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [step, phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const questions = useMemo(() => fields.filter(f => f.options?.length || f.slider), [fields]);
  // C, no one near: "or widen my search" — back to the distance question
  // (the community's slider), else to where they are.
  // Only the distance changes: its button then searches again straight away
  // (no other questions, no email step).
  const [widening, setWidening] = useState(false);
  const widenSearch = () => {
    const i = questions.findIndex(q => q.slider);
    setWidening(true);
    setPhase('questions');
    setStep(i >= 0 ? i + 1 : 0);
    setError('');
  };
  // Answered? (a slider's answer is a number, a list may be empty.)
  const hasAnswer = (v) => (Array.isArray(v) ? v.length > 0 : v !== '' && v != null);
  const field = step > 0 ? questions[step - 1] : null;
  // A slider starts already set to the community's default ("start", e.g.
  // 15 miles for golfers) — like dating apps — so it counts as answered and
  // shows in the lasso; they drag only to change it.
  useEffect(() => {
    if (field?.slider && answers[field.name] == null && Number.isFinite(field.start)) setAnswer(field.name, field.start);
  }, [field]); // eslint-disable-line react-hooks/exhaustive-deps
  const isLast = step === questions.length;

  const remember = (next) => {
    // Kept for the signup / profile steps to pre-fill later.
    try { localStorage.setItem(`ic_quickmatch_${communityId}`, JSON.stringify({ answers: next, place })); } catch { /* ignore */ }
  };

  const setAnswer = (name, value) => setAnswers(prev => {
    const next = { ...prev, [name]: value };
    remember(next);
    return next;
  });

  // "Other" in a tick-box list: ticking it shows a short text box, stored
  // in the list as "Other: …" (same as the profile form).
  const isOther = (o) => o === 'Other' || (typeof o === 'string' && o.startsWith('Other: '));
  const toggle = (name, option) => {
    const current = Array.isArray(answers[name]) ? answers[name] : [];
    const on = option === 'Other' ? current.some(isOther) : current.includes(option);
    setAnswer(name, on
      ? current.filter(o => (option === 'Other' ? !isOther(o) : o !== option))
      : [...current, option]);
  };
  const otherText = (name) => {
    const entry = (answers[name] || []).find(o => typeof o === 'string' && o.startsWith('Other: '));
    return entry ? entry.slice('Other: '.length) : '';
  };
  const setOtherText = (name, text) => {
    const rest = (answers[name] || []).filter(o => !isOther(o));
    const t = text.slice(0, 60);
    setAnswer(name, [...rest, t.trim() ? `Other: ${t}` : 'Other']);
  };

  const search = async () => {
    setPhase('searching');
    setError('');
    const started = Date.now();
    try {
      const r = await api(`/${communityId}/find`, {
        answers,
        place: place || found?.localArea,
        coords: found?.coordinates || coords,
        localArea: found?.localArea,
        // A skipped county ("Essex") searches the normal radius from its middle.
        broad: !!found?.broad && !found?.county,
        visitorId: getVisitorId(),
      });
      await new Promise(res => setTimeout(res, Math.max(0, MIN_SEARCH_MS - (Date.now() - started))));
      setResult(r);
      setShowFar(false);
      setPhase(r.total > 0 && !r.widenedTo ? 'results' : 'none');
      if (notifyAfterSearch.current || emailSaved) {
        notifyAfterSearch.current = false;
        api(`/${communityId}/notify`, { searchId: r.searchId, email, acceptTerms: termsAccepted, deleteIfInactive: deleteAfter3 })
          .then(() => {
            if (!emailSaved) track('Email given (before search)');
            setEmailSaved(true);
          })
          .catch((err) => { setEmailSaved(false); setError(err.message); });
      }
      track(r.total > 0 && !r.widenedTo
        ? `Result: ${r.total} within ${r.radiusMiles} mi`
        : r.widenedTo ? `Result: none near, nearest within ${r.widenedTo} mi` : 'Result: no one found');
    } catch (err) {
      setError(err.message);
      track(`Search error: ${err.message}`);
      setPhase('questions');
      setStep(0); // most errors are about the place
    }
  };

  const next = async () => {
    setError('');
    if (step === 0 && refining) {
      // "Which town or city in France?" — picked from the list already, or
      // look up what they typed (within that country).
      if (!found?.broad) {
        setRefining(null);
      } else if (!place.trim()) {
        setError(`Type a town or city in ${refining.name} — or tap Skip if you're not sure.`);
        return;
      } else {
        setBusy(true);
        const where = await findLocalArea({ query: place, countryCode: refining.countryCode }).catch(() => null);
        setBusy(false);
        if (!where || where.broad) {
          setError(`We couldn't find that in ${refining.name} — try another town, or skip.`);
          return;
        }
        setFound(where);
        setRefining(null);
        track(`Town in ${refining.name}: ${where.localArea}`);
      }
    } else if (step === 0) {
      if (!place.trim() && !coords && !found) {
        setError('Please add your town or postcode.');
        return;
      }
      if (!found) {
        setBusy(true);
        const where = await findLocalArea({ query: place, coords }).catch(() => null);
        setBusy(false);
        if (!where) {
          setError("We couldn't find that place — try a nearby town or your postcode.");
          return;
        }
        if (where.broad) {
          acceptBroad(where, 'Typed');
          return; // shows the "which town?" slide
        }
        setFound(where);
      }
    }
    if (widening) {
      setWidening(false);
      search();
    } else if (PRE_ASK && isLast && intro && !emailSaved && !preAsked.current) {
      // C: ask for their email before the search runs — once a visit.
      preAsked.current = true;
      setPhase('preask');
      track('Reached email step');
    } else if (isLast) search();
    else setStep(s => s + 1);
  };
  const preAskSubmit = (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    notifyAfterSearch.current = true;
    search();
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return setError("Your browser can't share location — please type your town instead.");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        const here = [pos.coords.longitude, pos.coords.latitude];
        setCoords(here);
        setPlace('');
        setError('');
        findLocalArea({ coords: here }).then(w => { if (w) setFound(w); }).catch(() => {});
        if (questions.length) setStep(1);
      },
      () => { setBusy(false); setError("Couldn't get your location — please type your town or postcode."); },
      { timeout: 8000, maximumAge: 10 * 60 * 1000 }
    );
  };

  const notify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api(`/${communityId}/notify`, {
        searchId: result.searchId,
        email,
        acceptTerms: termsAccepted,
        deleteIfInactive: deleteAfter3,
      });
      // C keeps them on the screen (a "✓ We'll email you…" line in place of
      // the ask); B moves on to its thank-you screen.
      if (intro) setEmailSaved(true);
      else setPhase('notified');
      track(`Email saved (${phase === 'results' ? 'after matches' : 'no one near'})`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // C's email ask (members found / no one near), under the heading: just
  // the box at first — the tick boxes and Submit unfold under it once they
  // start typing. Once saved: a "✓ We'll email you…" line in its place.
  const cEmailAsk = (where, lead) => (emailSaved ? (
    <p className="qm-film-text qm-film-text--saved qm-results-saved">✓ We&apos;ll email you at {email} when compatible {word} join.</p>
  ) : (
    <div className="qm-results-email">
      <p className="qm-results-lead">{lead}</p>
      <EmailInput
        className="qm-input"
        placeholder="Your email"
        value={email}
        onChange={setEmail}
        required
      />
      <div className={`qm-unfold${email.trim() ? ' qm-unfold--open' : ''}`} aria-hidden={!email.trim()}>
        <div className="qm-unfold-inner">
          {emailTicks}
          {error && <p className="qm-error">{error}</p>}
          <button type="submit" className="qm-primary" data-qm={`Email: submit (${where})`} disabled={busy}>{busy ? 'Saving…' : 'Submit'}</button>
        </div>
      </div>
    </div>
  ));

  // The nearest of the members shown, in miles, from their "About 40 miles
  // away" / "Under 5 miles away" labels.
  const nearestShownMiles = Math.min(...(result?.matches || []).map(m => Number(String(m.distance).match(/\d+/)?.[0]) || 0));
  // "Anywhere in the UK" (radius 1000): they're not all near — say "across the UK".
  const near = result?.radiusMiles >= 1000
    ? (country ? ` across the ${country}` : ' anywhere')
    : result?.localArea ? ` ${result.broad ? 'in' : 'near'} ${result.localArea}` : ' near you';

  const signUpToConnect = () => {
    // Remember who they wanted (by search + position — the server knows
    // which member that was) so signup can follow up with an introduction.
    try {
      localStorage.setItem(`ic_quickmatch_connect_${communityId}`, JSON.stringify({
        searchId: result?.searchId, index: connecting?.index, at: Date.now(),
      }));
    } catch { /* ignore */ }
    navigate(joinUrl);
  };

  // Terms (required) and "delete my details after 3 months" (optional) —
  // under every email box on the results screens.
  const emailTicks = (
    <>
      <label className="qm-tick">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => { setTermsAccepted(e.target.checked); if (e.target.checked) track('Accepted terms'); }}
          required
        />
        <span>
          I accept the <a href="/terms" target="_blank" rel="noopener noreferrer">terms and conditions</a>
          {' '}and the <a href="/privacy" target="_blank" rel="noopener noreferrer">privacy policy</a>
        </span>
      </label>
      <label className="qm-tick qm-tick--green">
        <input
          type="checkbox"
          checked={deleteAfter3}
          onChange={(e) => { setDeleteAfter3(e.target.checked); track(`${e.target.checked ? 'Ticked' : 'Unticked'} delete after 3 months`); }}
        />
        <span>Automatically delete my details after 3 months if no one suitable joins (optional)</span>
      </label>
    </>
  );

  // Fit estimate (from the server, 0–100): the best of those shown, in words.
  const bestFit = result?.matches?.length ? Math.max(...result.matches.map(m => m.fit || 0)) : null;
  const fitLabel = (pct) => (pct >= 80 ? 'Strong fit' : pct >= 65 ? 'Good fit' : pct >= 45 ? 'Possible fit' : 'Limited fit');

  // Suggestions carousel: which card is in view, and scrolling to one.
  const carouselRef = useRef(null);
  const [slide, setSlide] = useState(0);
  const onCarouselScroll = () => {
    const el = carouselRef.current;
    const card = el?.querySelector('.qm-match');
    if (!card) return;
    setSlide(Math.round(el.scrollLeft / (card.offsetWidth + 12)));
  };
  const slideTo = (i) => {
    const el = carouselRef.current;
    const cards = el?.querySelectorAll('.qm-match');
    if (!cards?.length) return;
    const target = cards[Math.max(0, Math.min(cards.length - 1, i))];
    el.scrollTo({ left: target.offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
  };

  const matchList = result?.matches?.length > 0 && (
    <ul className="qm-matches">
      {result.matches.map((m, i) => (
        <li key={i} className="qm-match">
          <span className="qm-avatar" aria-hidden="true">👤</span>
          <span className="qm-match-body">
            {m.fit != null && <span className="qm-match-fit">{m.fit}% fit</span>}
            {/* Waiting list: searched here and left their email, not signed up yet. */}
            <span><strong>{m.waitlist
              ? `${word.replace(/s$/, '').replace(/^\w/, c => c.toUpperCase())} on the waiting list`
              : 'Member'}</strong></span>
            {m.inCommon.length > 0 && <span className="qm-match-common">You both: {m.inCommon.join(' · ')}</span>}
            {/* Distance and age band, laid out like the other details ("Gender: Man"). */}
            {m.distance && (
              <span className="qm-match-detail">
                <span className="qm-match-detail-name">Distance:</span> {String(m.distance).replace(/^\w/, c => c.toLowerCase())}
              </span>
            )}
            {m.ageBand && (
              <span className="qm-match-detail">
                <span className="qm-match-detail-name">Age:</span> {m.ageBand}
              </span>
            )}
            {(m.details || []).map(d => (
              <span key={d.name} className="qm-match-detail">
                <span className="qm-match-detail-name">{d.name}:</span>{' '}
                {d.values.map((v, j) => {
                  const mine = answers[d.name];
                  const same = Array.isArray(mine) ? mine.includes(v) : mine === v;
                  return (
                    <React.Fragment key={v}>
                      {j > 0 && ', '}
                      <span className={same ? 'qm-match-same' : undefined}>{v}</span>
                    </React.Fragment>
                  );
                })}
              </span>
            ))}
            <span className="qm-match-hidden">{m.waitlist
              ? '⏳ This person has asked us to email them if someone who fits their profile expresses interest in connecting'
              : '🔒 Full bio and photo hidden — sign up to see them and fully connect'}</span>
            <span className="qm-match-actions">
              <button type="button" className="qm-match-join" data-qm={`Connect #${i + 1}`} onClick={() => setConnecting({ match: m, index: i, reason: 'connect' })}>
                Connect
              </button>
              {/* No bio or photo yet for the waiting list. */}
              {!m.waitlist && (
                <button type="button" className="qm-match-join qm-match-join--ghost" data-qm={`Show full profile #${i + 1}`} onClick={() => setConnecting({ match: m, index: i, reason: 'profile' })}>
                  Show full profile: bio &amp; photo
                </button>
              )}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );

  // Running record of what they've told us so far, built up as they go:
  // 📍 place, then each answer. Tapping one goes back to that step.
  const summary = [
    found?.localArea && { step: 0, label: `📍 ${found.localArea}` },
    ...questions.flatMap((q, i) => {
      const a = answers[q.name];
      const values = Array.isArray(a) ? a : (a ? [a] : []);
      return values.map(v => ({
        step: i + 1,
        label: q.slider
          ? `📏 ${/any distance/.test(sliderLabel(v, q)) ? 'Any distance' : `Within ${sliderLabel(v, q)}`}`
          : !Array.isArray(a) && /^\d+$/.test(v) ? `${q.name} ${v}` : v,
      }));
    }),
  ].filter(Boolean);
  const canEdit = phase === 'questions' || phase === 'none';
  // Progress dots — sit just above the Next button.
  const progressDots = (
    <div className="qm-progress" aria-hidden="true">
      {[0, ...questions.map((_, i) => i + 1)].map(i => (
        <span key={i} className={`qm-dot ${i <= step ? 'qm-dot--on' : ''}`} />
      ))}
    </div>
  );

  // A new answer pops in; when the lasso grows to fit it, the answers
  // already in it get a small nudge — each its own way (from its label).
  const drift = (label) => {
    let h = 0;
    for (const ch of String(label)) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    const r = (n) => ((h >>> n) % 1000) / 1000; // 0–1
    return {
      '--qm-dx': `${(r(1) * 5 - 2.5).toFixed(1)}px`,
      '--qm-dy': `${(r(4) * 4 - 2).toFixed(1)}px`,
      '--qm-rot': `${(r(7) * 2.4 - 1.2).toFixed(1)}deg`,
      '--qm-dur': `${(1.1 + r(10) * 0.7).toFixed(2)}s`,
      '--qm-wait': `${(r(13) * 0.25).toFixed(2)}s`,
    };
  };

  // Re-mounting the pills replays the wobble; labels seen before don't pop.
  const summaryKey = `${phase}-${step}-${summary.map(x => x.label).join('|')}`;
  const seenPills = useRef(new Set());
  const isNewPill = (label) => !seenPills.current.has(label);
  useEffect(() => { summary.forEach(x => seenPills.current.add(x.label)); });
  // Searching: the lasso closes in, ties their answers up and drops into
  // the pulsing circle (QuickMatchHero.css .qm-searching). Each pill gets
  // how far it is from the lasso's middle; the lasso how far from the pulse.
  const searchingRef = useRef(null);
  useLayoutEffect(() => {
    if (phase !== 'searching') return;
    const lasso = searchingRef.current?.querySelector('.qm-summary');
    const pulse = searchingRef.current?.querySelector('.qm-pulse');
    if (!lasso || !pulse) return;
    // Hold the card at its height, so once the lasso has gone the pulse
    // (and "Finding…") can settle into the middle of it.
    searchingRef.current.style.minHeight = `${searchingRef.current.offsetHeight}px`;
    lasso.style.setProperty('--qm-lasso-h', `${lasso.offsetHeight}px`);
    const box = lasso.getBoundingClientRect();
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;
    const p = pulse.getBoundingClientRect();
    lasso.style.setProperty('--qm-to-pulse', `${p.top + p.height / 2 - cy}px`);
    lasso.querySelectorAll('.qm-pill').forEach((el) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--qm-gx', `${(cx - (r.left + r.width / 2)) * 0.75}px`);
      el.style.setProperty('--qm-gy', `${(cy - (r.top + r.height / 2)) * 0.75}px`);
    });
  }, [phase]);

  // The lasso stretches from its last size to its new one (then wobbles),
  // so it looks like it's making room for the new answer.
  const lassoRef = useRef(null);
  const lassoSize = useRef(null);
  useLayoutEffect(() => {
    const el = lassoRef.current;
    if (!el) { lassoSize.current = null; return; }
    const now = { w: el.offsetWidth, h: el.offsetHeight };
    const was = lassoSize.current;
    // First time it appears: it unfurls from a dot, then the first answer
    // dissolves in (QuickMatchHero.css [data-first]).
    if (!was) el.dataset.first = '1';
    // Only when the answers actually make it bigger: it eases out from its
    // old size, and the answers already in it get a little nudge.
    else if (now.w > was.w + 2 || now.h > was.h + 2) {
      el.dataset.grow = '1';
      el.style.setProperty('--qm-from-sx', Math.max(0.6, was.w / now.w).toFixed(3));
      el.style.setProperty('--qm-from-sy', Math.max(0.5, was.h / now.h).toFixed(3));
    }
    lassoSize.current = now;
  }, [summaryKey]);

  const summaryRow = summary.length > 0 && (
    <div className="qm-summary" aria-label="Your answers so far" key={summaryKey} ref={lassoRef}>
      {summary.map((item, i) => (canEdit ? (
        <button
          key={item.label}
          style={drift(item.label)}
          type="button"
          className={`qm-pill${isNewPill(item.label) ? ' qm-pill--new' : ''}`}
          data-qm={`Summary: change ${item.label}`}
          onClick={() => { setWidening(false); setPhase('questions'); setStep(item.step); setError(''); }}
          title="Change"
        >
          {item.label}
        </button>
      ) : (
        <span key={item.label} style={drift(item.label)} className={`qm-pill qm-pill--static${isNewPill(item.label) ? ' qm-pill--new' : ''}`}>{item.label}</span>
      )))}
    </div>
  );

  return (
    <div className={`qm-card${floating ? ' qm-card--floating' : ''}${introStage === 'arriving' ? ' qm-card--from-intro' : ''}${intro ? (phase === 'intro' ? ' qm-card--c-intro' : ' qm-card--c-panel') : ''}`} data-phase={phase} aria-live="polite" ref={cardRef}>
      {/* Portalled to <body>: the card's transform / backdrop blur would
          otherwise trap a fixed-position pop-up inside the card. */}
      {connecting && createPortal(
        <div className="qm-modal-backdrop" onClick={() => setConnecting(null)}>
          <div className="qm-modal" role="dialog" aria-modal="true" aria-describedby="qm-modal-text" onClick={(e) => e.stopPropagation()}>
            {connecting.reason === 'profile' ? (
              <p id="qm-modal-text" className="qm-modal-text">
                To see this member&apos;s full profile — their bio and photo — you&apos;ll first need to sign up.
                It&apos;s free and takes about 3 minutes, and you can then connect with them too.
              </p>
            ) : connecting.match?.waitlist ? (
              // Someone on the waiting list (not signed up): we'll tell them.
              <p id="qm-modal-text" className="qm-modal-text">
                Sign up, and if you and this person seem a good fit, we&apos;ll let them know you&apos;ve joined!
                It&apos;s free and takes about 3 minutes.
              </p>
            ) : (
              <p id="qm-modal-text" className="qm-modal-text">
                To connect with this member (and others), you&apos;ll first need to sign up — it&apos;s free and takes
                about 3 minutes. Once you have, we&apos;ll introduce you if they&apos;re interested too.
              </p>
            )}
            <button type="button" className="qm-primary" data-qm={`Pop-up: sign up to ${connecting.reason === 'profile' ? 'see profile' : 'connect'} #${connecting.index + 1}`} data-qm-join="1" onClick={signUpToConnect} autoFocus>
              {connecting.reason === 'profile' ? 'Sign up to see their profile' : 'Sign up to connect'}
            </button>
            <button type="button" className="qm-link qm-modal-close" data-qm="Pop-up: not now" onClick={() => setConnecting(null)}>Not now</button>
          </div>
        </div>,
        document.body
      )}
      {phase === 'intro' && (
        <div className={`qm-intro${introStage === 'leaving' ? ' qm-intro--leaving' : ''}`}>
          <Link to={joinUrl} className="qm-primary" data-qm="Intro: join now">{intro_('quickMatchIntroJoin')}</Link>
          <button type="button" className="qm-secondary qm-intro-look" data-qm="Intro: see who's near first" onClick={lookFirst}>
            {intro_('quickMatchIntroLook')}
          </button>
        </div>
      )}

      {phase === 'questions' && (
        <>
          {summaryRow}

          {step === 0 && refining ? (
            <form onSubmit={(e) => {
              e.preventDefault();
              if (document.querySelector('.pac-container .pac-item-selected')) return;
              next();
            }}>
              <h2 className="qm-title">Which town or city in {refining.name}?</h2>
              <p className="qm-sub">So we can find people near you — skip if you&apos;re not sure yet.</p>
              <input
                key="qm-town"
                id="qm-place"
                ref={placeRef}
                className="qm-input"
                type="text"
                autoComplete="off"
                onFocus={attachAutocomplete}
                placeholder={`Town or city in ${refining.name}`}
                value={place}
                onChange={(e) => { setPlace(e.target.value); setFound(refining.where); }}
                autoFocus
              />
              {found && !found.broad && <p className="qm-found">📍 {found.localArea}</p>}
              {error && <p className="qm-error">{error}</p>}
              {progressDots}
              <div className="qm-nav">
                <button
                  type="button"
                  className="qm-link"
                  data-qm={`Skip town (stay with ${refining.name})`}
                  onClick={() => { setRefining(null); setPlace(refining.name); setError(''); setStep(1); }}
                >
                  Skip
                </button>
                <button type="submit" className="qm-primary qm-primary--inline" data-qm={`Next (town in ${refining.name})`} disabled={busy}>
                  {busy ? 'Finding…' : 'Next'}
                </button>
              </div>
            </form>
          ) : step === 0 ? (
            <form onSubmit={(e) => {
              e.preventDefault();
              // Enter picks a suggestion when the list is open — don't move on too.
              if (document.querySelector('.pac-container .pac-item-selected')) return;
              next();
            }}>
              <h2 className="qm-title">Find {word} near you</h2>
              {/* C: no sub-line or "Use my location" — just where they are. */}
              {!intro && <p className="qm-sub">A few quick taps and we&apos;ll show you who fits.</p>}
              <label className="qm-label" htmlFor="qm-place">Where are you?</label>
              <input
                id="qm-place"
                ref={placeRef}
                className="qm-input"
                type="text"
                autoComplete="off"
                onFocus={attachAutocomplete}
                placeholder={coords ? 'Using your location' : 'Town or postcode'}
                value={place}
                onChange={(e) => { setPlace(e.target.value); setCoords(null); setFound(null); }}
              />
              {found && <p className="qm-found">📍 {found.localArea}</p>}
              {!intro && (
                <button type="button" className="qm-link" data-qm="Use my location" onClick={useMyLocation} disabled={busy}>
                  {busy ? 'Finding you…' : '📍 Use my location'}
                </button>
              )}
              {error && <p className="qm-error">{error}</p>}
              {progressDots}
              <button type="submit" className="qm-primary" data-qm="Next (location)" disabled={busy}>{busy ? 'Finding…' : 'Next'}</button>
            </form>
          ) : (
            <div>
              <h2 className="qm-title qm-title--q">{field.name}</h2>
              {field.slider ? (
                // A number slider (htmlref 'slider'); range and unit from the
                // schema, the top meaning "that or further".
                <div className="qm-slider">
                  <div className="qm-slider-value">
                    {sliderLabel(hasAnswer(answers[field.name]) ? answers[field.name] : field.start, field)}
                  </div>
                  <div className="qm-slider-hint">Drag to adjust</div>
                  <input
                    type="range"
                    className="qm-range"
                    {...rangeInputProps(field)}
                    value={positionOfValue(hasAnswer(answers[field.name]) ? answers[field.name] : field.start, field)}
                    onChange={(e) => setAnswer(field.name, valueAtPosition(e.target.value, field))}
                    onMouseUp={(e) => track(`${field.name}: ${sliderLabel(valueAtPosition(e.target.value, field), field)}`)}
                    onTouchEnd={(e) => track(`${field.name}: ${sliderLabel(valueAtPosition(e.target.value, field), field)}`)}
                    aria-label={field.name}
                    aria-valuetext={sliderLabel(hasAnswer(answers[field.name]) ? answers[field.name] : field.start, field)}
                  />
                  <div className="qm-slider-scale" aria-hidden="true">
                    <span>{sliderLabel(field.min, field)}</span>
                    <span>{sliderLabel(field.max, field)}</span>
                  </div>
                </div>
              ) : field.multi ? (
                <>
                  <p className="qm-sub">Tap any that apply</p>
                  <div className="qm-chips">
                    {field.options.map(o => {
                      const on = o === 'Other' ? (answers[field.name] || []).some(isOther) : (answers[field.name] || []).includes(o);
                      return (
                        <button
                          key={o}
                          type="button"
                          className={`qm-chip ${on ? 'qm-chip--on' : ''}`}
                          aria-pressed={on}
                          data-qm={`${on ? 'Untick' : 'Tick'} ${field.name}: ${o}`}
                          onClick={() => toggle(field.name, o)}
                        >
                          {o}
                        </button>
                      );
                    })}
                  </div>
                  {field.options.includes('Other') && (answers[field.name] || []).some(isOther) && (
                    <input
                      className="qm-input qm-other"
                      type="text"
                      maxLength={60}
                      placeholder="Other — please say what"
                      value={otherText(field.name)}
                      onChange={(e) => setOtherText(field.name, e.target.value)}
                      onBlur={(e) => { if (e.target.value.trim()) track(`${field.name}: Other: ${e.target.value.trim()}`); }}
                      aria-label={`${field.name}: other`}
                    />
                  )}
                </>
              ) : field.options.length > 8 ? (
                <select
                  className="qm-input"
                  value={answers[field.name] || ''}
                  onChange={(e) => { setAnswer(field.name, e.target.value); track(`${field.name}: ${e.target.value || '(cleared)'}`); }}
                  aria-label={field.name}
                >
                  <option value="">Choose…</option>
                  {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <div className="qm-chips">
                  {field.options.map(o => (
                    <button
                      key={o}
                      type="button"
                      className={`qm-chip ${answers[field.name] === o ? 'qm-chip--on' : ''}`}
                      aria-pressed={answers[field.name] === o}
                      data-qm={`${field.name}: ${o}`}
                      onClick={() => setAnswer(field.name, answers[field.name] === o ? '' : o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}
              {progressDots}
              <div className="qm-nav">
                <button type="button" className="qm-link" data-qm={`Back (from ${field.name})`} onClick={() => { setWidening(false); setStep(s => s - 1); }}>← Back</button>
                <button
                  type="button"
                  className="qm-primary qm-primary--inline"
                  data-qm={`${isLast || widening ? 'Find' : (hasAnswer(answers[field.name]) ? 'Next' : 'Skip')} (${field.name})`}
                  onClick={next}
                >
                  {isLast || widening ? `Find ${word}` : (hasAnswer(answers[field.name]) ? 'Next' : 'Skip')}
                </button>
              </div>
            </div>
          )}
          <Link to={joinUrl} className="qm-skip" data-qm={`Or just join (step ${step})`}>or just join →</Link>
        </>
      )}

      {phase === 'searching' && (
        <div className="qm-searching" ref={searchingRef}>
          {summaryRow}
          <div className="qm-pulse" aria-hidden="true" />
          <h2 className="qm-title">Finding {word}{found?.localArea ? ` ${found.broad ? 'in' : 'near'} ${found.localArea}` : ' near you'}…</h2>
        </div>
      )}

      {phase === 'results' && result && intro && (
        // C: the same build-up as "no one near" (see `film`).
        <form onSubmit={notify} className="qm-results-c" ref={filmRef}>
          {/* The suggestions first: who fits (heading), how well, the members. */}
          <div data-film="0" className={`qm-carousel ${filmClass(0)}`}>
            <div className="qm-carousel-head">
              <h2 className="qm-title qm-results-title">
                {result.total === 1 ? `1 ${word.replace(/s$/, '')} who fits` : `${result.total} ${word} who fit`}{near}
              </h2>
              <p className="qm-results-daily">New members are joining daily!</p>
            </div>
            {/* The email ask, right under the heading: just the box at first —
                the tick boxes and Submit unfold under it once they start typing. */}
            {cEmailAsk('after matches', 'Leave your email and we\'ll let you know when more compatible people join')}
            {/* How good we think the fit is (the best of those shown), right under the heading. */}
            {bestFit != null && (
              <div className="qm-fit">
                <div className="qm-fit-head">
                  <span className="qm-fit-label">{fitLabel(bestFit)}</span>
                  <span className="qm-fit-pct">{bestFit}%</span>
                </div>
                <div className="qm-fit-bar" aria-hidden="true"><span style={{ width: `${bestFit}%` }} /></div>
                <p className="qm-fit-why">Our honest estimate — mostly age and distance, then what you have in common.</p>
              </div>
            )}
            <div className="qm-carousel-stage">
              <div className="qm-carousel-track" ref={carouselRef} onScroll={onCarouselScroll}>
                {matchList}
              </div>
              {result.matches.length > 1 && slide > 0 && (
                <button type="button" className="qm-carousel-btn qm-carousel-btn--prev" aria-label="Previous" data-qm="Carousel: previous" onClick={() => slideTo(slide - 1)}>‹</button>
              )}
              {result.matches.length > 1 && slide < result.matches.length - 1 && (
                <button type="button" className="qm-carousel-btn qm-carousel-btn--next" aria-label="Next" data-qm="Carousel: next" onClick={() => slideTo(slide + 1)}>›</button>
              )}
            </div>
            {result.matches.length > 1 && (
              <div className="qm-carousel-dots" aria-hidden="true">
                {result.matches.map((_, i) => <span key={i} className={`qm-dot${i === slide ? ' qm-dot--on' : ''}`} />)}
              </div>
            )}
          </div>
          <Link data-film="1" to={joinUrl} className={`qm-primary qm-results-join ${filmClass(1)}`} data-qm="Results: join to connect">Join to connect with them</Link>
          <p data-film="2" className={`qm-film-widen qm-results-widen ${filmClass(2)}`}>
            <button type="button" className="qm-reveal-link" data-qm="Widen my search (after matches)" onClick={widenSearch}>Widen my search</button>
          </p>
        </form>
      )}

      {phase === 'results' && result && !intro && (
        <>
          <h2 className="qm-title">{result.total === 1 ? `1 ${word.replace(/s$/, '')} who fits` : `${result.total} ${word} who fit`}{near}</h2>
          {/* How good we think the fit is (the best of those shown). */}
          {bestFit != null && (
            <div className="qm-fit">
              <div className="qm-fit-head">
                <span className="qm-fit-label">{fitLabel(bestFit)}</span>
                <span className="qm-fit-pct">{bestFit}%</span>
              </div>
              <div className="qm-fit-bar" aria-hidden="true"><span style={{ width: `${bestFit}%` }} /></div>
              <p className="qm-fit-why">Our honest estimate — mostly age and distance, then what you have in common.</p>
            </div>
          )}
          <form onSubmit={notify} className="qm-or-email">
            <p className="qm-sub">
              We can let you know when more {word} join who seem a better fit.<br />
              Just leave your email.
            </p>
            <div className="qm-email-row">
              <EmailInput
                className="qm-input"
                placeholder="Your email"
                value={email}
                onChange={setEmail}
                required
              />
            </div>
            {emailTicks}
            {error && <p className="qm-error">{error}</p>}
            <button type="submit" className="qm-secondary qm-or-email-submit" data-qm="Email: let me know (after matches)" disabled={busy}>{busy ? 'Saving…' : 'Submit my email'}</button>
          </form>
          {/* The suggestions, swiped through left to right. */}
          <div className="qm-carousel">
            <div className="qm-carousel-head">
              <span className="qm-carousel-title">Your suggestions:</span>
            </div>
            {/* Arrows over the cards, halfway down, like a swipe gallery. */}
            <div className="qm-carousel-stage">
              <div className="qm-carousel-track" ref={carouselRef} onScroll={onCarouselScroll}>
                {matchList}
              </div>
              {result.matches.length > 1 && slide > 0 && (
                <button type="button" className="qm-carousel-btn qm-carousel-btn--prev" aria-label="Previous" data-qm="Carousel: previous" onClick={() => slideTo(slide - 1)}>‹</button>
              )}
              {result.matches.length > 1 && slide < result.matches.length - 1 && (
                <button type="button" className="qm-carousel-btn qm-carousel-btn--next" aria-label="Next" data-qm="Carousel: next" onClick={() => slideTo(slide + 1)}>›</button>
              )}
            </div>
            {result.matches.length > 1 && (
              <div className="qm-carousel-dots" aria-hidden="true">
                {result.matches.map((_, i) => <span key={i} className={`qm-dot${i === slide ? ' qm-dot--on' : ''}`} />)}
              </div>
            )}
          </div>
        </>
      )}

      {phase === 'preask' && (
        // C: before the search runs — would they like an email when new members join?
        <form onSubmit={preAskSubmit} className="qm-preask">
          <p className="qm-film-text qm-film-text--ask">Before we run a search, would you like us to email you when new members join?</p>
          <p className="qm-film-text qm-film-text--small qm-preask-note">We are a new group and members are joining daily.</p>
          <div className="qm-none-email qm-none-email--c qm-film-in">
            <EmailInput
              className="qm-input"
              placeholder="Your email"
              value={email}
              onChange={setEmail}
              required
            />
            {emailTicks}
            {error && <p className="qm-error">{error}</p>}
            <button type="submit" className="qm-primary" data-qm="Email: yes, then search" disabled={busy}>Yes — email me, and search</button>
          </div>
          <button type="button" className="qm-secondary qm-preask-skip" data-qm="Email: no thanks, just search" onClick={() => { setError(''); search(); }}>
            No thanks, just search →
          </button>
        </form>
      )}

      {phase === 'none' && intro && (
        // C: like "members found", without the members — the news, "new
        // members daily", the email ask; then widen, further away, join.
        <form onSubmit={notify} className="qm-results-c qm-none-c" ref={filmRef}>
          <div data-film="0" className={filmClass(0)}>
            <div className="qm-carousel-head">
              <h2 className="qm-title qm-results-title">
                {result?.widenedTo
                  ? <>No {word} within {result.radiusMiles} {result.radiusMiles === 1 ? 'mile' : 'miles'}{result.localArea ? ` of ${result.localArea}` : ''} yet...</>
                  : <>No {word}{near} yet...</>}
              </h2>
              <p className="qm-results-daily">New members are joining daily!</p>
            </div>
            {cEmailAsk('no one near', 'Leave your email and we\'ll let you know when compatible people join')}
          </div>
          <p data-film="1" className={`qm-film-widen qm-results-widen ${filmClass(1)}`}>
            <button type="button" className="qm-reveal-link" data-qm="Widen my search (no one near)" onClick={widenSearch}>Widen my search</button>
          </p>
          <div data-film="2" className={filmClass(2)}>
            {result?.widenedTo && result.matches?.length > 0 && (
              showFar ? (
                <div className="qm-far-wrap">
                  <h3 className="qm-far-title">
                    {word.replace(/^\w/, c => c.toUpperCase())} {nearestShownMiles}+ miles {result.localArea ? `from ${result.localArea}` : 'away'}
                  </h3>
                  <div className="qm-box qm-far">{matchList}</div>
                </div>
              ) : (
                <p className="qm-reveal-line">
                  <button
                    type="button"
                    className="qm-reveal-link"
                    data-qm="Reveal further-away members"
                    onClick={() => {
                      setShowFar(true);
                      setTimeout(() => document.querySelector('.qm-far-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
                    }}
                  >
                    Show {word} further away
                  </button>
                </p>
              )
            )}
            <Link to={joinUrl} className="qm-skip qm-none-join" data-qm="Join now, first in line">or join now and be first in line →</Link>
          </div>
        </form>
      )}

      {phase === 'none' && !intro && (
        <form onSubmit={notify} className="qm-none">
          {intro ? (
            // C: the lines, stacking up one by one (see `film`).
            <div className="qm-film">
              {/* The news and "new group" together in a white-outlined box. */}
              <div className="qm-film-box">
                <p data-film="0" className={`qm-film-text qm-film-text--first ${filmClass(0)}`}>
                  {result?.widenedTo
                    ? <>No {word} within {result.radiusMiles} {result.radiusMiles === 1 ? 'mile' : 'miles'}{result.localArea ? ` of ${result.localArea}` : ''} </>
                    : <>No {word}{near} </>}
                  <span className="qm-yet">yet...</span>
                </p>
              </div>
              {emailSaved
                ? <p data-film="1" className={`qm-film-text qm-film-text--saved ${filmClass(1)}`}>✓ We&apos;ll email you at {email} when new {word} join.</p>
                : <p data-film="1" className={`qm-film-text qm-film-text--ask ${filmClass(1)}`}>Would you like us to let you know when more compatible members join?</p>}
              <p data-film="2" className={`qm-film-widen ${filmClass(2)}`}>
                <button type="button" className="qm-reveal-link" data-qm="Widen my search (no one near)" onClick={widenSearch}>{emailSaved ? 'Widen my search' : 'or widen my search'}</button>
              </p>
            </div>
          ) : (
            <>
              {/* First just the news… */}
              <h2 className="qm-title qm-fade-in">
                {result?.widenedTo
                  ? <>No {word} within {result.radiusMiles} {result.radiusMiles === 1 ? 'mile' : 'miles'}{result.localArea ? ` of ${result.localArea}` : ''} yet.</>
                  : <>No {word}{near} yet.</>}
              </h2>
            </>
          )}
          {/* …then, a moment later, the email sign-up… */}
          {!(intro && emailSaved) && (
          <div data-film="email" className={`qm-none-email${intro ? ` qm-none-email--c ${film.done ? 'qm-film-in' : 'qm-film-wait'}` : ''}`}>
            {!intro && (
              <p className="qm-sub">
                <span className="qm-line">We can let you know when others join who are nearer.</span>
                <span className="qm-line">Just leave your email.</span>
              </p>
            )}
            {!intro && result?.lastJoinedAt && <LastJoined at={result.lastJoinedAt} />}
            <EmailInput
              className="qm-input"
              placeholder="Your email"
              value={email}
              onChange={setEmail}
              required
            />
            {emailTicks}
            {error && <p className="qm-error">{error}</p>}
            <button type="submit" className="qm-primary" data-qm="Email: submit (no one near)" disabled={busy}>{busy ? 'Saving…' : 'Submit'}</button>
          </div>
          )}
          {/* …and, if anyone's further away: "Or show them" — the members
              only when they ask, one card at a time, under how far they are. */}
          {result?.widenedTo && result.matches?.length > 0 && (
            showFar ? (
              <div className="qm-far-wrap">
                <h3 className="qm-far-title">
                  {word.replace(/^\w/, c => c.toUpperCase())} {nearestShownMiles}+ miles {result.localArea ? `from ${result.localArea}` : 'away'}
                </h3>
                <div className="qm-box qm-far">{matchList}</div>
              </div>
            ) : (
              <div className={`qm-show-far${intro ? (film.done ? ' qm-film-in' : ' qm-film-wait') : ''}`}>
                <p className="qm-or">Or</p>
                <p className="qm-reveal-line">
                  <button
                    type="button"
                    className="qm-reveal-link"
                    data-qm="Reveal further-away members"
                    onClick={() => {
                      setShowFar(true);
                      setTimeout(() => document.querySelector('.qm-far-wrap')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
                    }}
                  >
                    Show {word} further away
                  </button>
                </p>
              </div>
            )
          )}
          <Link to={joinUrl} className={`qm-skip qm-none-join${intro ? (film.done ? ' qm-film-in' : ' qm-film-wait') : ''}`} data-qm="Join now, first in line">or join now and be first in line →</Link>
        </form>
      )}

      {phase === 'notified' && (
        <>
          <h2 className="qm-title">Thanks — you&apos;re on the list</h2>
          <p className="qm-sub">We&apos;ll email you when someone who fits joins{near}.</p>
          <Link to={joinUrl} className="qm-primary" data-qm="Join after email">Join now and set up your profile</Link>
        </>
      )}
    </div>
  );
};

export default QuickMatchHero;
